import { useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { SessionIdentity } from "@pustak-lms/contracts";
import { AccountSessionContext, formatAccountRole } from "./account-context";

export function ProfilePage({ identity, surface, preview = false, footer }: {
  identity: SessionIdentity;
  surface: "platform" | "manage" | "learn";
  preview?: boolean;
  footer?: ReactNode;
}) {
  const account = useContext(AccountSessionContext);
  const [name, setName] = useState(identity.user.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [invalidName, setInvalidName] = useState(false);
  const [saved, setSaved] = useState(false);
  const saving = useRef(false);
  useEffect(() => { setName(identity.user.name); }, [identity.user.id, identity.user.name]);
  const role = formatAccountRole(surface === "platform" ? identity.user.role : identity.membership?.memberType ?? identity.user.role);
  const initials = identity.user.name.trim().split(/\s+/).map(part => part[0]).join("").slice(0, 2).toUpperCase() || "?";
  const normalizedName = name.trim();
  const changed = normalizedName !== identity.user.name;
  const readOnly = preview || !account?.updateProfile;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const updateProfile = account?.updateProfile;
    if (readOnly || !updateProfile || saving.current || !changed || account?.busy) return;
    setSaved(false); setError(""); setInvalidName(false);
    if (!normalizedName || normalizedName.length > 100 || /[\u0000-\u001f\u007f-\u009f]/.test(normalizedName)) {
      setInvalidName(true);
      setError("Enter a full name of 1–100 characters without control characters.");
      return;
    }
    saving.current = true; setBusy(true);
    try {
      await updateProfile(normalizedName);
      setName(normalizedName); setSaved(true);
    } catch {
      setError("Unable to save your profile. Check your connection and session, then try again.");
    } finally {
      saving.current = false; setBusy(false);
    }
  }

  return <div className="profile-page">
    <header className="page-header"><div><p className="eyebrow">Your account</p><h1>My profile</h1><p>Manage your personal details and view your account access.</p></div></header>
    <section className="panel profile-summary" aria-label="Profile summary">
      <span className="avatar profile-avatar" aria-hidden="true">{initials}</span>
      <div><h2>{identity.user.name}</h2><p>{identity.user.email}</p><span className="profile-role">{role}</span></div>
    </section>
    {preview ? <p className="profile-preview-note">You are viewing your own profile in Student Preview. <a href="/manage/profile">Edit in Manage</a></p> : null}
    <section className="panel" aria-labelledby="profile-details-heading">
      <h2 id="profile-details-heading">Personal details</h2>
      <form className="profile-form" onSubmit={event => void submit(event)} aria-busy={busy}>
        <div className="profile-field"><label htmlFor="profile-name">Full name</label>
          <input id="profile-name" name="name" autoComplete="name" value={name} required maxLength={100} readOnly={readOnly} disabled={busy} aria-invalid={invalidName ? true : undefined} aria-describedby={invalidName ? "profile-name-help profile-error" : "profile-name-help"} onChange={event => { setName(event.target.value); setError(""); setInvalidName(false); setSaved(false); }} />
          <p id="profile-name-help" className="muted">Your name is shared across every institute where you use this account. Use up to 100 characters.</p>
        </div>
        <div className="profile-field"><label htmlFor="profile-email">Login email</label><input id="profile-email" type="email" value={identity.user.email} readOnly aria-describedby="profile-email-help" /><p id="profile-email-help" className="muted">This is the email you use to sign in.</p></div>
        {!readOnly ? <div className="profile-actions"><button type="submit" className="button button-primary" disabled={busy || account?.busy || !changed}>{busy ? "Saving…" : "Save changes"}</button></div> : null}
        {error ? <p id="profile-error" role="alert" className="auth-error">{error}</p> : null}
        {saved ? <p role="status">Profile saved.</p> : null}
      </form>
    </section>
    <section className="panel" aria-labelledby="profile-access-heading"><h2 id="profile-access-heading">Account access</h2>
      <div className="profile-access-fields">
        <div className="profile-field"><label htmlFor="profile-role">Role</label><input id="profile-role" value={role} readOnly /></div>
        {surface !== "platform" && identity.organization ? <div className="profile-field"><label htmlFor="profile-organization">Institute</label><input id="profile-organization" value={identity.organization.name} readOnly /></div> : null}
      </div>
      <p className="muted">Your role determines what you can access in this workspace.</p>
    </section>
    <section className="panel profile-security" aria-labelledby="profile-security-heading"><div><h2 id="profile-security-heading">Security</h2><p className="muted">Keep your account secure by updating your password.</p></div><a className="button button-secondary" href={preview ? "/manage/change-password" : account?.changePasswordHref ?? `${surface === "platform" ? "" : `/${surface}`}/change-password`}>Change password</a></section>
    {footer}
  </div>;
}
